import React, { ElementType, useMemo } from 'react';
import { ScopedCssBaseline, ScopedCssBaselineProps, ThemeProvider, Box, BoxProps, Theme } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache, { Options } from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import containerPlugin from './plugins/stylisContainerPlugin';
import { theme as skodaTheme } from '../../materialui/theme/theme';
import '../../materialui/theme/theme.extensions';

export type SkodaThemeProviderProps = {
  children?: React.ReactNode;
  /**
   * Theme to be used
   */
  theme?: Partial<Theme>;
  /**
   * Disable adding extra ScopedCssBaseline as CssBaseline is being used
   */
  globalBaseline?: boolean;
  /**
   * Changes @media queries to @container queries,
   */
  widget?: boolean;
  /**
   * Options for the cache creation function by Emotion
   */
  createCacheOptions?: Options;
  /**
   * Props passed to container used by the widget
   */
  containerProps?: BoxProps<ElementType>;
} & ScopedCssBaselineProps;

/**
 * Wrapper of ThemeProvider that additionally provide Scoped CssBaseline
 */
export const SkodaThemeProvider: React.FC<SkodaThemeProviderProps> = ({
  children,
  theme = skodaTheme,
  globalBaseline = false,
  widget = false,
  createCacheOptions,
  containerProps,
  ...rest
}) => {
  return (
    <CacheContainer direction={theme.direction} widget={widget} createCacheOptions={createCacheOptions}>
      <ThemeProvider theme={theme}>
        <WidgetContainer widget={widget} {...containerProps}>
          {globalBaseline ? (
            children
          ) : (
            <ScopedCssBaseline {...rest} {...(theme.direction === 'rtl' ? { dir: 'rtl' } : {})}>
              {children}
            </ScopedCssBaseline>
          )}
        </WidgetContainer>
      </ThemeProvider>
    </CacheContainer>
  );
};

/**
 * Wrapper providing CacheProvider necessary for the stylis plugin optimization
 */
function CacheContainer({
  children,
  direction,
  widget,
  createCacheOptions,
}: {
  children: React.ReactNode;
  direction: Theme['direction'];
  widget: boolean;
  createCacheOptions?: Options;
}) {
  const cache = useMemo(() => {
    // in case we are not using RTL or widget, we don't need to generate cache
    if (direction !== 'rtl' && !widget) {
      return undefined;
    }
    return createCache({
      // inject first
      prepend: true,
      // cache based on the plugins
      key: `${widget ? 'w' : ''}${direction === 'rtl' ? 'rtl' : 'css'}`,
      stylisPlugins: [
        prefixer,
        // transform left to right
        ...(direction === 'rtl' ? [rtlPlugin] : []),
        // transform @media to @container queries
        ...(widget ? [containerPlugin] : []),
      ],
      ...(createCacheOptions || {}),
    });
  }, [direction, widget, createCacheOptions?.key]);

  return cache ? <CacheProvider value={cache}>{children}</CacheProvider> : <>{children}</>;
}

/**
 * Wrapper providing necessary containerType: 'inline-size' style to activate @container queries
 */
function WidgetContainer({ children, sx, widget, ...rest }: { widget: boolean } & BoxProps) {
  return widget ? (
    <Box sx={{ containerType: 'inline-size', ...sx }} {...rest}>
      {children}
    </Box>
  ) : (
    <>{children}</>
  );
}
